import type {
  AdminActivityFeedItem,
  AdminExecution,
  AutomationCancellationDetails,
} from '@/types/admin.types';

export const AUTOMATION_CANCELLATION_SCHEMA_VERSION = 1 as const;

export const AUTOMATION_CANCELLATION_CODES = {
  DSN_BULLETINS_PENDING_RETRY: 'dsn.bulletins_pending_retry',
  DSN_CONTROL_BLOCKED_RETRY: 'dsn.control_blocked_retry',
  OPERATOR_CANCELLED: 'automation.operator_cancelled',
  DUPLICATE_SUPPRESSED: 'automation.duplicate_suppressed',
  TECHNICAL_INTERRUPTION: 'automation.technical_interruption',
  PENDING_ORPHANED: 'automation.pending_orphaned',
} as const;

export type AutomationExecutionTone = 'success' | 'warning' | 'danger' | 'info' | 'neutral';

export interface AutomationExecutionPresentation {
  label: string;
  description?: string;
  tone: AutomationExecutionTone;
  cancellation?: AutomationCancellationDetails;
  unknownCancellation?: boolean;
}

const KNOWN_CATEGORIES = new Set([
  'business_prerequisite',
  'operator_action',
  'deduplicated',
  'technical_interruption',
]);

const KNOWN_INITIATORS = new Set(['system', 'administrator']);

const CANCELLATION_PRESENTATIONS: Record<
  string,
  Omit<AutomationExecutionPresentation, 'cancellation'>
> = {
  [AUTOMATION_CANCELLATION_CODES.DSN_BULLETINS_PENDING_RETRY]: {
    label: 'En attente des bulletins',
    description: 'Les bulletins ne sont pas encore prêts. Une reprise automatique est prévue.',
    tone: 'info',
  },
  [AUTOMATION_CANCELLATION_CODES.DSN_CONTROL_BLOCKED_RETRY]: {
    label: 'Contrôle DSN bloquant',
    description: 'Une anomalie de contrôle doit être examinée avant la prochaine reprise.',
    tone: 'danger',
  },
  [AUTOMATION_CANCELLATION_CODES.OPERATOR_CANCELLED]: {
    label: 'Annulée par un administrateur',
    description: 'Cette exécution a été interrompue volontairement par un administrateur.',
    tone: 'warning',
  },
  [AUTOMATION_CANCELLATION_CODES.DUPLICATE_SUPPRESSED]: {
    label: 'Doublon ignoré',
    description: 'Une exécution équivalente existait déjà. Aucune action n’est requise.',
    tone: 'neutral',
  },
  [AUTOMATION_CANCELLATION_CODES.TECHNICAL_INTERRUPTION]: {
    label: 'Interruption technique',
    description: 'L’interruption doit être examinée avant un nouveau lancement.',
    tone: 'danger',
  },
  [AUTOMATION_CANCELLATION_CODES.PENDING_ORPHANED]: {
    label: 'Travail planifié non démarré',
    description:
      'La tâche n’a pas rejoint sa file d’exécution. Un examen technique est requis avant toute relance.',
    tone: 'danger',
  },
};

const STANDARD_PRESENTATIONS: Record<string, AutomationExecutionPresentation> = {
  success: { label: 'Réussie', tone: 'success' },
  completed: { label: 'Réussie', tone: 'success' },
  failed: { label: 'Échec', tone: 'danger' },
  error: { label: 'Erreur', tone: 'danger' },
  running: { label: 'En cours', tone: 'info' },
  active: { label: 'En cours', tone: 'info' },
  pending: { label: 'En attente', tone: 'warning' },
  waiting: { label: 'En attente', tone: 'warning' },
};

function asRecord(value: unknown): Record<string, unknown> | undefined {
  return value !== null && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : undefined;
}

export function readAutomationCancellation(
  value: unknown
): AutomationCancellationDetails | undefined {
  const cancellation = asRecord(value);

  if (
    !cancellation ||
    cancellation.schemaVersion !== AUTOMATION_CANCELLATION_SCHEMA_VERSION ||
    typeof cancellation.code !== 'string' ||
    !KNOWN_CATEGORIES.has(String(cancellation.category)) ||
    !KNOWN_INITIATORS.has(String(cancellation.initiatedBy)) ||
    typeof cancellation.retryPlanned !== 'boolean' ||
    typeof cancellation.requiresAttention !== 'boolean'
  ) {
    return undefined;
  }

  return cancellation as unknown as AutomationCancellationDetails;
}

export function getExecutionCancellation(
  execution: Pick<AdminExecution, 'details'>
): AutomationCancellationDetails | undefined {
  return readAutomationCancellation(execution.details?.cancellation);
}

export function getAutomationExecutionPresentation(
  execution: Pick<AdminExecution, 'status' | 'details'>
): AutomationExecutionPresentation {
  if (execution.status !== 'cancelled') {
    return (
      STANDARD_PRESENTATIONS[execution.status] ?? {
        label: 'État à examiner',
        tone: 'warning',
      }
    );
  }

  const cancellation = getExecutionCancellation(execution);
  const knownPresentation = cancellation
    ? CANCELLATION_PRESENTATIONS[cancellation.code]
    : undefined;

  if (!cancellation || !knownPresentation) {
    return {
      label: 'Annulation à examiner',
      description:
        'La cause n’est pas disponible ou n’est pas reconnue par cette version du backoffice.',
      tone: 'warning',
      cancellation,
      unknownCancellation: true,
    };
  }

  return {
    ...knownPresentation,
    tone:
      cancellation.requiresAttention &&
      (knownPresentation.tone === 'info' || knownPresentation.tone === 'neutral')
        ? 'warning'
        : knownPresentation.tone,
    cancellation,
  };
}

export function shouldOfferManualRetry(
  execution: Pick<AdminExecution, 'status' | 'details' | 'retryBlocked'>
): boolean {
  if (
    execution.retryBlocked ||
    execution.details?.effectContract?.retryPolicy === 'blocked_sensitive_effect_may_have_occurred'
  ) {
    return false;
  }
  if (execution.status === 'failed' || execution.status === 'error') return true;
  if (execution.status !== 'cancelled') return false;

  const cancellation = getExecutionCancellation(execution);
  if (cancellation?.code === AUTOMATION_CANCELLATION_CODES.PENDING_ORPHANED) {
    return false;
  }
  return Boolean(cancellation?.requiresAttention && cancellation.retryPlanned === false);
}

export function shouldShowTechnicalDetail(
  execution: Pick<AdminExecution, 'status' | 'details' | 'error'>
): boolean {
  if (!execution.error) return false;
  if (execution.status === 'failed' || execution.status === 'error') return true;

  return (
    execution.status === 'cancelled' &&
    getExecutionCancellation(execution)?.code ===
      AUTOMATION_CANCELLATION_CODES.TECHNICAL_INTERRUPTION
  );
}

export function getActivityCancellation(
  activity: AdminActivityFeedItem
): AutomationCancellationDetails | undefined {
  const details = asRecord(activity.details);
  return readAutomationCancellation(activity.cancellation ?? details?.cancellation);
}

export function getCancellationActivityLabel(activity: AdminActivityFeedItem): string | undefined {
  if (activity.type !== 'automation_cancelled') return undefined;

  return getAutomationExecutionPresentation({
    status: 'cancelled',
    details: { cancellation: getActivityCancellation(activity) ?? {} },
  }).label;
}
